import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { ArrowRight, Sparkles, Settings } from "lucide-react";
import { toast } from "sonner";

const INDUSTRIES = ["Technology", "Retail", "E-commerce", "Hospitality", "Food & Beverage", "Construction", "Manufacturing", "Healthcare", "Education", "Logistics", "Real Estate", "Agriculture", "Services", "Beauty & Wellness", "Automotive"];
const COUNTRIES = ["UAE", "Saudi Arabia", "Qatar", "Bahrain", "Oman", "Kuwait", "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt"];

export default function MatchesPage() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrefs, setShowPrefs] = useState(false);
  const [profile, setProfile] = useState({ industries: [], countries: [], budget_min: 100000, budget_max: 10000000 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: p }] = await Promise.all([api.get("/matches"), api.get("/profile")]);
      setMatches(m.matches);
      setProfile({ industries: p.industries || [], countries: p.countries || [], budget_min: p.budget_min ?? 100000, budget_max: p.budget_max ?? 10000000 });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); else navigate("/login"); }, [user, load, navigate]);

  const savePrefs = async () => {
    await api.put("/profile", profile);
    toast.success("Preferences saved");
    setShowPrefs(false);
    load();
  };

  const toggle = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  if (!user) return null;

  return (
    <div data-testid="matches-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="dv-pill mb-3"><Sparkles size={13} /> AI Matching Engine</span>
          <h1 className="dv-display text-3xl md:text-5xl mb-2">Your top matches.</h1>
          <p className="text-slate-600">Sorted by match score — based on your industries, countries and budget.</p>
        </div>
        <button onClick={() => setShowPrefs(!showPrefs)} className="dv-btn dv-btn-secondary dv-btn-sm" data-testid="match-prefs-btn">
          <Settings size={14} /> Preferences
        </button>
      </div>

      {showPrefs && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 dv-shadow-soft" data-testid="prefs-panel">
          <h3 className="font-bold text-lg mb-4">Matching preferences</h3>
          <div className="space-y-5">
            <div>
              <label className="dv-label">Industries of interest</label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map(i => (
                  <button key={i} type="button" onClick={() => setProfile(p => ({ ...p, industries: toggle(p.industries, i) }))}
                    className={`text-sm px-3 py-1.5 rounded-full border ${profile.industries.includes(i) ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    {i}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="dv-label">Countries of interest</label>
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map(c => (
                  <button key={c} type="button" onClick={() => setProfile(p => ({ ...p, countries: toggle(p.countries, c) }))}
                    className={`text-sm px-3 py-1.5 rounded-full border ${profile.countries.includes(c) ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="dv-label">Budget min (USD)</label><input type="number" className="dv-input" value={profile.budget_min} onChange={e => setProfile({ ...profile, budget_min: Number(e.target.value) })} /></div>
              <div><label className="dv-label">Budget max (USD)</label><input type="number" className="dv-input" value={profile.budget_max} onChange={e => setProfile({ ...profile, budget_max: Number(e.target.value) })} /></div>
            </div>
            <button onClick={savePrefs} className="dv-btn dv-btn-primary" data-testid="save-prefs-btn">Save & rematch</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-slate-500">Computing your matches...</div>
      ) : matches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-slate-600 mb-4">No matches yet. Set your preferences to get started.</p>
          <button onClick={() => setShowPrefs(true)} className="dv-btn dv-btn-primary">Set preferences</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="matches-grid">
          {matches.map((m, i) => (
            <Link to={m.link} key={i} className="dv-card group" data-testid={`match-${m.id}`}>
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                {m.image_url ? <img src={m.image_url} alt={m.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-4xl font-bold text-blue-600">{m.title?.[0] || "?"}</div>}
                <div className="absolute top-3 right-3 bg-white rounded-full px-3 py-1 flex items-center gap-1.5 dv-shadow-soft">
                  <div className={`w-2 h-2 rounded-full ${m.score >= 80 ? "bg-emerald-500" : m.score >= 60 ? "bg-amber-500" : "bg-slate-400"}`}></div>
                  <span className="text-sm font-bold text-slate-900">{m.score}</span>
                  <span className="text-xs text-slate-500">match</span>
                </div>
              </div>
              <div className="p-5">
                <span className="dv-pill mb-2 capitalize">{m.type}</span>
                <h3 className="font-bold text-lg leading-snug">{m.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{m.subtitle}</p>
                {m.price ? <p className="font-bold text-blue-600 mt-3">{format(m.price)}</p> : null}
                {m.reasons?.length ? (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                    {m.reasons.slice(0, 3).map((r, i) => <p key={i} className="text-xs text-slate-500 flex items-center gap-1"><span className="text-emerald-500">✓</span> {r}</p>)}
                  </div>
                ) : null}
                <div className="mt-3 text-blue-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">View details <ArrowRight size={13} /></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
